import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { FileArchive, Download, Eye, RefreshCw, TrendingUp, TrendingDown, Wallet, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const fmtFCFA = (v) => (Number(v) || 0).toLocaleString('fr-FR').replace(/,/g, ' ') + ' FCFA';

function fmtPeriode(mois) {
  if (!mois) return '';
  const [y, m] = mois.split('-');
  const date = new Date(Number(y), Number(m) - 1, 1);
  return date.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
}

function fmtGenerated(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });
  } catch { return iso.slice(0, 16); }
}

function previousMonthISO(n = 1) {
  const d = new Date();
  d.setMonth(d.getMonth() - n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

export default function RapportsMensuelsTab({ user }) {
  const isAdmin = (user?.role || '').toLowerCase() === 'admin';
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [generateMonth, setGenerateMonth] = useState(previousMonthISO(1));

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/rapports-mensuels`);
      setItems(data.items || []);
    } catch (e) {
      toast.error('Erreur de chargement des rapports');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handlePreview = (r) => {
    const token = localStorage.getItem('token') || '';
    window.open(`${API}/api/rapports-mensuels/${r.id}/pdf?token=${encodeURIComponent(token)}`, '_blank');
  };

  const handleDownload = async (r) => {
    try {
      const { data } = await axios.get(`${API}/api/rapports-mensuels/${r.id}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url; a.download = `Rapport-Comptable-${r.mois}.pdf`;
      a.click(); URL.revokeObjectURL(url);
    } catch { toast.error('Téléchargement impossible'); }
  };

  const handleGenerate = async () => {
    if (!generateMonth) return;
    setGenerating(true);
    try {
      const { data } = await axios.post(
        `${API}/api/rapports-mensuels/generer-maintenant`,
        null,
        { params: { mois: generateMonth, force: true } }
      );
      toast.success(`Rapport ${data.mois_label} généré`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur de génération');
    } finally { setGenerating(false); }
  };

  return (
    <div className="space-y-4">
      <Card className="border-blue-100 bg-gradient-to-r from-blue-50 to-emerald-50">
        <CardContent className="p-4 sm:p-5">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-start gap-3">
              <div className="w-12 h-12 rounded-xl bg-blue-600 flex items-center justify-center flex-shrink-0">
                <FileArchive className="h-6 w-6 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-[#1C2A46]">Clôture comptable automatique</h3>
                <p className="text-sm text-slate-600">
                  Chaque <strong>1er du mois à 02h00</strong>, le système archive automatiquement le bilan, le compte de résultat et la trésorerie du mois précédent. Tous les rapports sont téléchargeables en PDF.
                </p>
              </div>
            </div>
            {isAdmin && (
              <div className="flex items-center gap-2 flex-shrink-0">
                <input
                  type="month"
                  value={generateMonth}
                  onChange={(e) => setGenerateMonth(e.target.value)}
                  className="border border-slate-300 rounded-md px-3 py-1.5 text-sm h-9"
                  data-testid="month-select"
                />
                <Button
                  onClick={handleGenerate}
                  disabled={generating || !generateMonth}
                  className="bg-blue-600 hover:bg-blue-700 gap-2"
                  data-testid="generate-now"
                >
                  {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                  Générer maintenant
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
        </div>
      ) : items.length === 0 ? (
        <Card>
          <CardContent className="text-center py-16 text-slate-500">
            <FileArchive className="h-12 w-12 mx-auto mb-3 text-slate-300" />
            <p className="font-semibold text-slate-700 mb-1">Aucun rapport archivé pour le moment</p>
            <p className="text-sm">Les rapports apparaîtront automatiquement chaque début de mois.</p>
            {isAdmin && <p className="text-xs mt-3">Vous pouvez générer manuellement un rapport ci-dessus.</p>}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {items.map(r => (
            <RapportRow key={r.id} rapport={r} onPreview={() => handlePreview(r)} onDownload={() => handleDownload(r)} />
          ))}
        </div>
      )}
    </div>
  );
}

function RapportRow({ rapport, onPreview, onDownload }) {
  const k = rapport.kpis || {};
  const isBenefice = (k.resultat_net || 0) >= 0;
  const soldePositif = (k.solde_mois || 0) >= 0;

  return (
    <Card className="hover:shadow-md transition-all" data-testid={`rapport-${rapport.mois}`}>
      <CardContent className="p-4">
        <div className="flex flex-col lg:flex-row gap-3 lg:items-center">
          {/* Date du mois en avant-plan */}
          <div className="flex items-center gap-3 flex-shrink-0">
            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-blue-600 to-blue-800 flex flex-col items-center justify-center text-white">
              <span className="text-[10px] uppercase opacity-80">{rapport.mois.split('-')[0]}</span>
              <span className="text-xl font-bold leading-none">{rapport.mois.split('-')[1]}</span>
            </div>
            <div>
              <p className="font-bold text-[#1C2A46] capitalize">{fmtPeriode(rapport.mois)}</p>
              <p className="text-xs text-slate-500">Période : {rapport.date_debut} → {rapport.date_fin}</p>
              <p className="text-[11px] text-slate-400 mt-0.5">Généré le {fmtGenerated(rapport.generated_at)}</p>
            </div>
          </div>

          {/* KPIs */}
          <div className="flex flex-1 gap-3 flex-wrap">
            <KpiMini icon={TrendingUp}  label="Produits"      value={k.total_produits}      color="text-emerald-700" />
            <KpiMini icon={TrendingDown} label="Charges"       value={k.total_charges}       color="text-rose-700" />
            <KpiMini
              icon={isBenefice ? TrendingUp : TrendingDown}
              label={isBenefice ? 'Bénéfice' : 'Perte'}
              value={k.resultat_net}
              color={isBenefice ? 'text-emerald-700 font-bold' : 'text-rose-700 font-bold'}
              highlight
            />
            <KpiMini icon={Wallet} label="Solde trésorerie" value={k.solde_mois} color={soldePositif ? 'text-emerald-700' : 'text-rose-700'} />
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <Button variant="outline" size="sm" onClick={onPreview} className="gap-1.5" data-testid={`preview-rapport-${rapport.mois}`}>
              <Eye className="h-4 w-4" /> Aperçu
            </Button>
            <Button size="sm" onClick={onDownload} className="bg-emerald-600 hover:bg-emerald-700 gap-1.5" data-testid={`download-rapport-${rapport.mois}`}>
              <Download className="h-4 w-4" /> PDF
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function KpiMini({ icon: Icon, label, value, color = 'text-slate-700', highlight = false }) {
  return (
    <div className={`flex-1 min-w-[140px] ${highlight ? 'bg-slate-50 border border-slate-200 rounded-lg p-2' : ''}`}>
      <div className="flex items-center gap-1.5">
        <Icon className={`h-3.5 w-3.5 ${color}`} />
        <span className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">{label}</span>
      </div>
      <p className={`text-sm ${color} mt-0.5`}>{fmtFCFA(value)}</p>
    </div>
  );
}
